import { Image } from '@tiptap/extension-image';
import { TableKit } from '@tiptap/extension-table';
import { Placeholder } from '@tiptap/extensions';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { hasModifier } from '../lib/keyboard';
import { Icon } from './Icon';
import type { IconName } from './Icon';

/**
 * Der Texteditor der Anwendung -- F1-10, K-28.
 *
 * **Einer fuer alle Masken.** Vorher gab es zwei: am Ticket drei Knoepfe, die
 * HTML-Tags um die Auswahl legten, in der Wissensdatenbank sieben andere mit
 * anderem Umfang -- und an sechs weiteren Stellen (Mailvorlagen, Signaturen,
 * Ankuendigungen, Textbausteine, Ticketvorlagen, Sachverhalt) ein nacktes
 * Textfeld, in das jemand HTML tippen musste. Wer zwischen diesen Masken
 * wechselte, musste jedes Mal neu lernen, was geht.
 *
 * **Warum jetzt doch eine Abhaengigkeit.** Die bisherige Fassung hatte einen
 * guten Grund, keine zu nehmen: `contenteditable` mit `document.execCommand`
 * erzeugt in jedem Browser anderes HTML -- Safari macht aus einer Fettung ein
 * `<span style="font-weight:bold">`, und das entfernt `sanitise_html` beim
 * Speichern wieder. Der Anwender saehe seine Auszeichnung schreiben und nach
 * dem Absenden verschwinden; ein WYSIWYG, das stillschweigend Formatierung
 * verliert, ist schlimmer als ein ehrliches Textfeld.
 *
 * ProseMirror loest genau das: Der Editor hat ein **Schema**. Er kann nichts
 * erzeugen, was nicht darin steht. Die Liste unten ist deshalb kein
 * Vorschlag, sondern eine Zusicherung -- und sie ist dieselbe wie
 * `ALLOWED_TAGS` in `core/html.py` und in `RichText`. Drei Stellen, eine
 * Liste: Wer eine aendert, aendert alle.
 *
 * **Die Bereinigung im Server bleibt trotzdem.** Der Editor bestimmt, was ein
 * Bearbeiter erzeugen kann -- nicht, was am Endpunkt ankommt. Ein Skript
 * gegen die API schickt, was es will.
 */

/** Welche Werkzeuge eine Maske anbietet. */
export interface EditorFeatures {
  /** Bild einfuegen. Nur dort, wo es einen Anhangsbestand gibt. */
  image?: boolean;
  /** Tabelle einfuegen. */
  table?: boolean;
  /** Ueberschriften. In einer Signatur haben sie nichts zu suchen. */
  headings?: boolean;
}

const FULL: Required<EditorFeatures> = { image: true, table: true, headings: true };

interface RichTextEditorProps {
  /** Das HTML, wie es gespeichert wird. */
  value: string;
  onChange: (html: string) => void;
  /** Beschriftung des Schreibbereichs fuer vorlesende Programme. */
  ariaLabel: string;
  /** Die Kennung, auf die ein `<label>` daneben zeigt. */
  id?: string;
  placeholder?: string;
  features?: EditorFeatures;
  disabled?: boolean;
  /**
   * Laedt ein Bild hoch und liefert die Adresse, unter der es liegt.
   *
   * Ohne diese Funktion erscheint der Bildknopf nicht -- ein Knopf, der
   * nichts tun kann, ist schlimmer als keiner.
   */
  onUploadImage?: (file: File) => Promise<string>;
  /** Tastenkuerzel zum Absenden (Strg/Cmd + Eingabe). */
  onSubmit?: () => void;
  /** Wie hoch der Schreibbereich mindestens ist, in Zeilen. */
  minRows?: number;
  /**
   * Bekommt eine Funktion, die Text an der Schreibmarke einfuegt -- fuer
   * Masken, die etwas von aussen einsetzen, etwa einen Platzhalter per Klick
   * (F3-21). Eine Referenz statt eines Werts: Das Einfuegen ist eine
   * Handlung, kein Zustand.
   */
  insertRef?: MutableRefObject<((text: string) => void) | null>;
}

export function RichTextEditor({
  value,
  onChange,
  ariaLabel,
  id,
  placeholder,
  features,
  disabled = false,
  onUploadImage,
  onSubmit,
  minRows = 6,
  insertRef,
}: RichTextEditorProps) {
  const { t } = useTranslation();
  const imageInput = useRef<HTMLInputElement>(null);
  const submitRef = useRef(onSubmit);
  submitRef.current = onSubmit;

  const enabled = { ...FULL, ...features };

  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        // Die Ueberschriftgrade der Anwendung. `h1` fehlt mit Absicht: Die
        // Seitenueberschrift gehoert der Anwendung, nicht dem Beitrag.
        heading: enabled.headings ? { levels: [2, 3, 4] } : false,
        link: {
          openOnClick: false,
          autolink: true,
          // Dieselben Schemata wie im Server und in `RichText`.
          protocols: ['http', 'https', 'mailto', 'tel'],
          HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
        },
      }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      ...(enabled.image ? [Image.configure({ inline: false })] : []),
      ...(enabled.table ? [TableKit.configure({ table: { resizable: false } })] : []),
    ],
    content: value,
    editorProps: {
      attributes: {
        class: 'editor__content prose prose--rich',
        'aria-label': ariaLabel,
        ...(id ? { id } : {}),
        style: `min-height: ${String(minRows * 1.5 + 1)}rem`,
      },
      handleKeyDown: (_view, event) => {
        // Absenden mit der Zusatztaste und der Eingabetaste. Die
        // Eingabetaste allein bleibt der Absatz -- ein Feld, in dem sie
        // abschickt, verschickt halbe Saetze.
        if (hasModifier(event) && event.key === 'Enter' && submitRef.current) {
          event.preventDefault();
          submitRef.current();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: instance }) => {
      // `isEmpty` statt eines Vergleichs mit `<p></p>`: ProseMirror haelt
      // immer mindestens einen leeren Absatz vor. Ohne diesen Schritt bekaeme
      // jede Maske "<p></p>" statt einer leeren Zeichenkette -- und jede
      // Pruefung auf "ist etwas eingegeben" ginge daneben.
      onChange(instance.isEmpty ? '' : instance.getHTML());
    },
  });

  // Der Editor haelt seinen Inhalt selbst. Von aussen kommt er nur, wenn die
  // Maske ihn ersetzt -- ein geladener Entwurf, ein eingefuegter Baustein,
  // ein Wechsel des bearbeiteten Datensatzes. Der Vergleich mit `getHTML()`
  // ist die Bedingung dafuer, dass der Schreibende dabei nicht den Cursor
  // verliert.
  useEffect(() => {
    const current = editor.isEmpty ? '' : editor.getHTML();
    if (current === value) return;
    editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!insertRef) return;
    insertRef.current = (text: string) => {
      editor.chain().focus().insertContent(text).run();
    };
    return () => {
      insertRef.current = null;
    };
  }, [editor, insertRef]);

  return (
    <div className={`editor${disabled ? ' editor--disabled' : ''}`}>
      <Toolbar
        editor={editor}
        features={enabled}
        canUploadImage={onUploadImage !== undefined}
        onPickImage={() => imageInput.current?.click()}
      />

      <EditorContent editor={editor} />

      {onUploadImage ? (
        <input
          ref={imageInput}
          type="file"
          accept="image/*"
          hidden
          aria-label={t('editor.image')}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file) return;
            void onUploadImage(file).then((src) => {
              editor.chain().focus().setImage({ src, alt: file.name }).run();
            });
          }}
        />
      ) : null}
    </div>
  );
}

/** Ein Knopf der Werkzeugleiste. */
function ToolButton({
  icon,
  label,
  active = false,
  disabled = false,
  onClick,
}: {
  icon: IconName;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`editor__tool${active ? ' editor__tool--on' : ''}`}
      // `aria-pressed` und nicht nur eine Farbe: Ob eine Auszeichnung gerade
      // gilt, muss auch ein vorlesendes Programm sagen koennen.
      aria-pressed={active}
      aria-label={label}
      title={label}
      disabled={disabled}
      // Der Knopf darf den Schreibbereich nicht die Auswahl kosten -- sonst
      // wirkt die Auszeichnung auf nichts.
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onClick={onClick}
    >
      <Icon name={icon} />
    </button>
  );
}

function Toolbar({
  editor,
  features,
  canUploadImage,
  onPickImage,
}: {
  editor: Editor;
  features: Required<EditorFeatures>;
  canUploadImage: boolean;
  onPickImage: () => void;
}) {
  const { t } = useTranslation();

  // Der Zustand der Knoepfe haengt an der Auswahl im Editor. `useEditorState`
  // waere der feinere Weg; ein Zaehler, der bei jeder Aenderung hochlaeuft,
  // ist der kuerzere und hier ausreichend.
  const [, setTick] = useState(0);
  useEffect(() => {
    const bump = () => {
      setTick((value) => value + 1);
    };
    editor.on('transaction', bump);
    return () => {
      editor.off('transaction', bump);
    };
  }, [editor]);

  const block = currentBlock(editor);

  return (
    <div className="editor__toolbar" role="toolbar" aria-label={t('editor.formatting')}>
      {/* Ein natives `<select>` und nicht das `Select` der Anwendung: Das
          ist ein Suchfeld mit Aufklappliste und waere fuer vier feste
          Absatzformate in einer Werkzeugleiste zu gross. */}
      {features.headings ? (
        <select
          className="editor__block"
          aria-label={t('editor.blockStyle')}
          value={block}
          onChange={(event) => {
            applyBlock(editor, event.target.value);
          }}
        >
          <option value="p">{t('editor.paragraph')}</option>
          <option value="h2">{t('editor.heading2')}</option>
          <option value="h3">{t('editor.heading3')}</option>
          <option value="h4">{t('editor.heading4')}</option>
        </select>
      ) : null}

      <span className="editor__sep" />

      <ToolButton
        icon="bold"
        label={t('editor.bold')}
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolButton
        icon="italic"
        label={t('editor.italic')}
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolButton
        icon="underline"
        label={t('editor.underline')}
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      />
      <ToolButton
        icon="strike"
        label={t('editor.strike')}
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      />

      <span className="editor__sep" />

      <ToolButton
        icon="bulletList"
        label={t('editor.bulletList')}
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolButton
        icon="orderedList"
        label={t('editor.orderedList')}
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />

      <span className="editor__sep" />

      <ToolButton
        icon="quote"
        label={t('editor.quote')}
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      />
      <ToolButton
        icon="code"
        label={t('editor.code')}
        active={editor.isActive('codeBlock')}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      />

      <span className="editor__sep" />

      <ToolButton
        icon="link"
        label={t('editor.link')}
        active={editor.isActive('link')}
        onClick={() => {
          promptForLink(editor, t('editor.linkPrompt'));
        }}
      />
      {features.image && canUploadImage ? (
        <ToolButton icon="image" label={t('editor.image')} onClick={onPickImage} />
      ) : null}
      {features.table ? (
        <ToolButton
          icon="table"
          label={t('editor.table')}
          onClick={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          }
        />
      ) : null}

      <span className="editor__spacer" />

      <ToolButton
        icon="undo"
        label={t('editor.undo')}
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      />
      <ToolButton
        icon="redo"
        label={t('editor.redo')}
        disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      />
    </div>
  );
}

function currentBlock(editor: Editor): string {
  for (const level of [2, 3, 4] as const) {
    if (editor.isActive('heading', { level })) return `h${String(level)}`;
  }
  return 'p';
}

function applyBlock(editor: Editor, value: string) {
  const chain = editor.chain().focus();
  if (value === 'p') {
    chain.setParagraph().run();
    return;
  }
  chain.setHeading({ level: Number(value.slice(1)) as 2 | 3 | 4 }).run();
}

/**
 * Fragt nach der Adresse eines Verweises.
 *
 * `window.prompt` und kein eigener Dialog: Der Fall ist selten, und ein
 * Dialog mit Fokusfalle, Abbruch und Tastaturbedienung waere fuer eine
 * einzelne Zeile mehr Bauteil als Nutzen. Wird der Verweis haeufiger
 * gebraucht, ist das der Zeitpunkt, ihn zu bauen.
 */
function promptForLink(editor: Editor, message: string) {
  const previous: unknown = editor.getAttributes('link').href;
  const entered = window.prompt(message, typeof previous === 'string' ? previous : 'https://');

  // Abgebrochen: nichts tun. Leer bestaetigt: Verweis entfernen.
  if (entered === null) return;
  if (entered.trim() === '') {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    return;
  }

  editor.chain().focus().extendMarkRange('link').setLink({ href: entered.trim() }).run();
}
